/**
 * TD Kit Addon Validator Web Component
 * Global validation for kit-addon cart logic.
 * Addons can only be added when their specific parent kit is in the cart.
 * 
 * Usage in theme.liquid:
 * <td-kit-addon-validator></td-kit-addon-validator>
 */

if (!customElements.get('td-kit-addon-validator')) {
  class TDKitAddonValidator extends HTMLElement {
    constructor() {
      super();
      this.kitProducts = [];
      this.addonToKit = {};
      this.kitsInCart = new Set();
      this.addonsInCart = new Set();
      this.initialized = false;
      this.cartUpdateUnsubscriber = null;
      this.isRemovingOrphans = false;
      this.disabledMessage = this.dataset.disabledMessage || 'Add Kit To Purchase';
      this.kitDisabledMessage = 'Only One Kit Per Checkout';
      this.addonDisabledMessage = 'Addon Already In Cart';
    }

    connectedCallback() {
      // Get mapping from global object set by Liquid snippet
      if (window.TDKitAddonMap) {
        this.kitProducts = window.TDKitAddonMap.kitProducts || [];
        this.addonToKit = window.TDKitAddonMap.addonToKit || {};
      }

      // If no kits configured, nothing to validate
      if (this.kitProducts.length === 0) {
        this.initialized = true;
        return;
      }

      // Initial cart check
      this.checkCart().then(() => {
        this.initialized = true;
        this.updateAllAddonButtons();
        this.updateAllKitButtons();
        this.dispatchReadyEvent();
      });

      // Subscribe to cart updates
      this.subscribeToCartUpdates();
      
      // Expose instance globally for external access
      window.tdKitAddonValidator = this;
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    subscribeToCartUpdates() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.handleCartUpdate(event);
        });
      }
    }

    // Handle cart update
    handleCartUpdate(event) {
      // Skip if this update was triggered by us removing orphans
      if (event?.source === 'td-kit-addon-validator') {
        return;
      }

      this.checkCart().then((cartData) => {
        this.updateAllAddonButtons();
        this.updateAllKitButtons();
        
        // Check for orphaned addons and remove them
        if (cartData && !this.isRemovingOrphans) {
          this.removeOrphanedAddons(cartData);
        }
      });
    }

    // Check the cart for kits and addons
    async checkCart() {
      try {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        this.updateKitsInCart(cartData);
        this.updateAddonsInCart(cartData);
        return cartData;
      } catch (error) {
        console.error('TDKitAddonValidator: Error fetching cart', error);
        return null;
      }
    }


    // Check if an addon is orphaned (has no valid kit in cart)
    isAddonOrphaned(addonVariantId) {
      const variantIdStr = String(addonVariantId);
      const validKitIds = this.addonToKit[variantIdStr];

      // If no valid kit mapping, it's not orphaned (can exist independently)
      if (!validKitIds || !Array.isArray(validKitIds) || validKitIds.length === 0) {
        return false;
      }

      // Check if any valid kit is in cart
      return !validKitIds.some(kitId => this.isKitInCart(kitId));
    }

    // Remove orphaned addons from the cart, side effect of removing a kit from the cart
    async removeOrphanedAddons(cartData) {
      if (!cartData || !cartData.items || this.isRemovingOrphans) return;

      const orphanedVariantIds = [];

      cartData.items.forEach((item) => {
        const variantIdStr = String(item.variant_id);
        
        if (this.isAddon(variantIdStr)) {
          // Only remove if orphaned (no valid kit), not if just already in cart
          if (this.isAddonOrphaned(variantIdStr)) {
            orphanedVariantIds.push(item.variant_id);
          }
        }
      });

      if (orphanedVariantIds.length > 0) {
        this.isRemovingOrphans = true;
        try {
          await this.removeCartItemsByVariantId(orphanedVariantIds);
        } finally {
          this.isRemovingOrphans = false;
        }
      }
    }

    // Remove cart items by variant id
    async removeCartItemsByVariantId(variantIds) {
      const updates = {};
      variantIds.forEach(variantId => {
        updates[variantId] = 0;
      });

      try {
        // Update cart
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });
        
        const cartData = await response.json();
        this.updateKitsInCart(cartData);
        this.updateAddonsInCart(cartData);

        // Fetch sections for cart re-render (including TD Header cart count)
        const sectionsResponse = await fetch(`${window.routes?.cart_url || '/cart'}?sections=cart-drawer,cart-icon-bubble,td-cart-count`);
        const sections = await sectionsResponse.json();

        // Update cart drawer sections WITHOUT opening it
        this.updateCartSections(sections, cartData.item_count === 0);
        
        // Update TD Header cart count
        this.updateTDHeaderCartCount(cartData.item_count);

        // Publish event for other listeners
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'td-kit-addon-validator',
            cartData: cartData
          });
        }
      } catch (error) {
        console.error('TDKitAddonValidator: Error removing orphaned addons', error);
      }
    }

    // Update TD Header cart count
    updateTDHeaderCartCount(itemCount) {
      const cartCountElements = document.querySelectorAll('.td-header__cart-count');
      cartCountElements.forEach(el => {
        el.textContent = itemCount;
      });
    }

    // Update cart sections, mostly for empty cart state
    updateCartSections(sections, isEmpty) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartIconBubble = document.getElementById('cart-icon-bubble');

      // Update cart drawer content
      if (cartDrawer && sections['cart-drawer']) {
        const drawerContent = cartDrawer.querySelector('#CartDrawer');
        if (drawerContent) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer');
          if (newContent) {
            drawerContent.innerHTML = newContent.innerHTML;
          }
        }
        
        // Toggle is-empty class
        cartDrawer.classList.toggle('is-empty', isEmpty);
        
        // Re-attach overlay click handler
        const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => cartDrawer.close());
        }
      }

      // Update cart icon bubble
      if (cartIconBubble && sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        if (newBubble) {
          cartIconBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }

    // Update kits in cart
    updateKitsInCart(cartData) {
      this.kitsInCart.clear();

      if (cartData && cartData.items) {
        cartData.items.forEach(item => {
          if (this.kitProducts.includes(item.product_id)) {
            this.kitsInCart.add(item.product_id);
          }
        });
      }
    }

    // Update addons in cart
    updateAddonsInCart(cartData) {
      this.addonsInCart.clear();

      if (cartData && cartData.items) {
        cartData.items.forEach(item => {
          const variantIdStr = String(item.variant_id);
          if (this.isAddon(variantIdStr)) {
            this.addonsInCart.add(variantIdStr);
          }
        });
      }
    }

    // Check if a kit is in the cart
    isKitInCart(kitProductId) {
      return this.kitsInCart.has(kitProductId);
    }

    // Check if an addon can be added to the cart
    canAddAddon(addonVariantId) {
      const variantIdStr = String(addonVariantId);
      
      // First check if this addon is already in cart
      if (this.isAddonInCart(variantIdStr)) {
        return false;
      }

      // Then check if a valid kit is in cart
      const validKitIds = this.addonToKit[variantIdStr];

      if (!validKitIds || !Array.isArray(validKitIds) || validKitIds.length === 0) {
        return true;
      }

      return validKitIds.some(kitId => this.isKitInCart(kitId));
    }

    // Check if an addon is in the cart
    isAddonInCart(addonVariantId) {
      return this.addonsInCart.has(String(addonVariantId));
    }

    // Get valid kit ids for an addon
    getValidKitIds(addonVariantId) {
      return this.addonToKit[String(addonVariantId)] || [];
    }

    // Check if a variant is an addon
    isAddon(variantId) {
      return String(variantId) in this.addonToKit;
    }

    // Check if any kit is in the cart
    hasKitInCart() {
      return this.kitsInCart.size > 0;
    }

    // Check if a button is a kit button (has data-add-to-cart but is NOT an addon)
    isKitButton(button) {
      // Must have data-add-to-cart attribute
      if (!button.hasAttribute('data-add-to-cart')) {
        return false;
      }

      // If it has a variant ID, check if it's an addon
      const variantId = button.dataset.variantId;
      if (variantId && this.isAddon(variantId)) {
        return false; // It's an addon, not a kit
      }

      // It's a kit button if it's not an addon
      return true;
    }

    // Update all addon buttons
    updateAllAddonButtons() {
      const addonButtons = document.querySelectorAll('[data-add-to-cart][data-variant-id]');

      addonButtons.forEach(button => {
        const variantId = button.dataset.variantId;

        if (this.isAddon(variantId)) {
          this.updateButtonState(button, variantId);
        }
      });
      
      this.dispatchUpdateEvent();
    }

    // Update all kit buttons
    updateAllKitButtons() {
      const allButtons = document.querySelectorAll('[data-add-to-cart]');

      allButtons.forEach(button => {
        if (this.isKitButton(button)) {
          this.updateKitButtonState(button);
        }
      });
    }

    // Update button state where addons can happen
    updateButtonState(button, variantId) {
      const variantIdStr = String(variantId);
      const canAdd = this.canAddAddon(variantId);
      const isAlreadyInCart = this.isAddonInCart(variantIdStr);

      // Always save original HTML on first encounter
      if (!button.dataset.originalHtml) {
        button.dataset.originalHtml = button.innerHTML;
      }

      if (canAdd && !isAlreadyInCart) {
        button.innerHTML = button.dataset.originalHtml;
        button.removeAttribute('disabled');
        button.classList.remove('td-addon-disabled');
        button.setAttribute('aria-disabled', 'false');
      } else {
        // Determine which message to show
        let message = this.disabledMessage;
        if (isAlreadyInCart) {
          message = this.addonDisabledMessage;
        }
        
        button.innerHTML = `<span>${message}</span>`;
        button.setAttribute('disabled', 'disabled');
        button.classList.add('td-addon-disabled');
        button.setAttribute('aria-disabled', 'true');
      }
    }

    // Update kit button state
    updateKitButtonState(button) {
      const hasKit = this.hasKitInCart();

      // Always save original HTML on first encounter
      if (!button.dataset.originalKitHtml) {
        button.dataset.originalKitHtml = button.innerHTML;
      }

      if (hasKit) {
        // A kit is already in cart, disable this kit button
        button.innerHTML = `<span>${this.kitDisabledMessage}</span>`;
        button.setAttribute('disabled', 'disabled');
        button.classList.add('td-kit-disabled');
        button.setAttribute('aria-disabled', 'true');
      } else {
        // No kit in cart, enable this kit button
        button.innerHTML = button.dataset.originalKitHtml;
        button.removeAttribute('disabled');
        button.classList.remove('td-kit-disabled');
        button.setAttribute('aria-disabled', 'false');
      }
    }

    // Dispatch ready event
    dispatchReadyEvent() {
      document.dispatchEvent(new CustomEvent('td-kit-addon-validator:ready', {
        detail: { kitsInCart: Array.from(this.kitsInCart) }
      }));
    }

    // Dispatch update event
    dispatchUpdateEvent() {
      document.dispatchEvent(new CustomEvent('td-kit-addon-validator:update', {
        detail: { kitsInCart: Array.from(this.kitsInCart) }
      }));
    }
  }

  customElements.define('td-kit-addon-validator', TDKitAddonValidator);
}
